import React, { useEffect, useState } from "react";
import { Spin, Button, Modal, Select, Space, Typography, message, Flex } from "antd";
import moment from "moment";
import { EpisodeEvent } from "./types";
import { generateEventId } from "./utils";
import { authAxios } from "../../utils/session_utils";
import { LabelText, ModalDivider, ModalHeader, TextMuted } from "./StyledComponents";
import { FundProjectionScreenOutlined } from "@ant-design/icons";

export const getEpisodeOptions = async (
	data: any,
	successCallback: (result: any) => void,
	errorCallback: (error: any) => void
) => {
	try {
		const config = {
			method: "POST",
			url: `${process.env.REACT_APP_BACK_END_API}/scheduling/episode-options`,
			headers: {
				"Content-Type": "application/json",
			},
			data: JSON.stringify(data),
		};
		const response = await authAxios(config);
		if (!response.data.error) {
			successCallback(response.data);
		} else {
			errorCallback(response.data);
		}
	} catch (error) {
		console.error("getEpisodeOptions ~ error:", error);
		errorCallback(error);
	}
};

// Utility to create time options in 30-min intervals
const generateTimeSlots = (start = 0, end = 24) => {
	const slots = [];
	for (let h = start; h < end; h++) {
		slots.push(`${String(h).padStart(2, "0")}:00`);
		slots.push(`${String(h).padStart(2, "0")}:30`);
	}
	return slots;
};

const timeOptions = generateTimeSlots();
export type EpisodeOption = {
	duration: number;
	label: string;
	value: string;
};

type EpisodeEventModalProps = {
	visible: boolean;
	initialData: EpisodeEvent | null;
	onClose: (data: EpisodeEvent | null) => void;
	onDelete: (oldEventID: string) => void;
};

type SelectedEpisode = {
	episodeId: string;
	seriesId: string;
	start: Date;
	end: Date;
	startHourString: string;
	endHourString: string;
};

const EpisodeEventModal = ({ visible, initialData, onClose, onDelete }: EpisodeEventModalProps) => {
	const [seriesId, setSeriesId] = useState<string | null>(initialData?.id || null);
	const [episodeOptionsLoading, setEpisodeOptionsLoading] = useState<boolean>(false);
	const [selectedEpisode, setSelectedEpisode] = useState<SelectedEpisode | null>(null);
	const [oldEventId, setOldEventId] = useState<string | null>(initialData?.id || null);
	const [episodeOptions, setEpisodeOptions] = useState<EpisodeOption[]>([]);

	const handleSubmit = () => {
		if(selectedEpisode === null) {
			message.error('Please select an episode');
			return;
		}
		onClose({
			id: generateEventId(selectedEpisode.episodeId, selectedEpisode.start, selectedEpisode.end),
			episodeId: selectedEpisode.episodeId,
			seriesId: seriesId || "",
			oldEventId: oldEventId || "",
			start: moment(selectedEpisode.start)
				.set({
					hour: Number(selectedEpisode.startHourString?.split(":")[0]),
					minute: Number(selectedEpisode.startHourString?.split(":")[1]),
				})
				.toDate(),
			end: moment(selectedEpisode.end)
				.set({
					hour: Number(selectedEpisode.endHourString?.split(":")[0]),
					minute: Number(selectedEpisode.endHourString?.split(":")[1]),
				})
				.toDate(),
			startHourString: selectedEpisode.startHourString,
			endHourString: selectedEpisode.endHourString,
		});
	};

	const fetchEpisodeOptions = async () => {
    if (initialData && initialData.seriesId) {
			// Simulate fetching episodes for the series
			const data = {
				seriesId: initialData.seriesId,
				distributionPlatforms: initialData.selectedDistributionPlatforms || [],
			};
			setEpisodeOptionsLoading(true);
			await getEpisodeOptions(
				data,
				(result: any) => {
					if (result && result.length > 0) {
						const options = result.map((episode: any) => ({
							label: (
									<Flex justify="space-between" align="center" style={{ padding: "8px 0" }}>
										<Flex gap={6} align="center">
											<b>{episode.refId}</b>
											<TextMuted>{episode.title}</TextMuted>
										</Flex>
										<TextMuted>{Math.round(episode.duration / 60)} mins</TextMuted>
									</Flex>
								),
							value: `${episode.refId} ${episode.title} (${Math.round(episode.duration / 60)} mins)`,
							duration: episode.duration,
						}));
						setEpisodeOptions(options);
					} else {
						setEpisodeOptions([]);
						message.warning("No episodes found for the selected series.");
					}
				},
				(error: any) => {
					console.error("Error fetching episode options:", error);
					message.error("Failed to fetch episode options.");
				}
			);
			setEpisodeOptionsLoading(false);
		} else {
			setEpisodeOptions([]);
		}
	};

	useEffect(() => {
		if (initialData) {
			setSeriesId(initialData.seriesId);
			setSelectedEpisode({
				episodeId: initialData.episodeId,
				seriesId: initialData.seriesId,
				start: initialData.start,
				end: initialData.end,
				startHourString: initialData.startHourString,
				endHourString: initialData.endHourString,
			});
			setOldEventId(initialData.id);
		}
		// Fetch episode options based on seriesId and distribution platforms
		fetchEpisodeOptions();
	}, [initialData]);

	const handleCancel = () => {
		onClose(null); // or pass empty data if needed
	};

	const handleDelete = () => {
		onDelete(oldEventId || "");
	};

	return (
		<Modal
			open={visible}
			title={
				<ModalHeader>
					<FundProjectionScreenOutlined />
					Episode Details
				</ModalHeader>
			}
			onOk={handleSubmit}
			onCancel={handleCancel}
			okButtonProps={{ disabled: !seriesId || !selectedEpisode }}
      		width={700}
			footer={() => (
				<Flex justify="space-between" align="center" style={{ marginTop: "24px" }}>
					<Button key="delete" type="primary" danger onClick={handleDelete}>
						Delete
					</Button>
					<Flex gap={8}>
						<Button key="back" onClick={handleCancel}>
							Cancel
						</Button>
						<Button key="submit" type="primary" onClick={handleSubmit}>
							Submit
						</Button>
					</Flex>
				</Flex>
			)}
		>
			<ModalDivider />
			<Space direction="vertical" size={1} style={{ width: "100%", margin: "20px 0 8px 0" }}>
				<LabelText>Episode</LabelText>
				<Spin spinning={episodeOptionsLoading} tip="Loading episodes...">
					<Select
						showSearch
						placeholder="Search or select an episode"
						value={selectedEpisode?.episodeId || undefined}
						onChange={value => {
							const selected = episodeOptions.find(option => option.value === value);
							if (selected) {
								// Calculate end time based on duration
								const durationInSeconds = selected.duration || 0;
								const startTime = moment(initialData?.start || new Date());
								// console.log('startTime', startTime, 'durationInSeconds', durationInSeconds);
								const endTime = startTime.clone().add(durationInSeconds, "seconds");
								// endTime.add(durationInSeconds, 'seconds');
								// console.log('endTime', endTime.format('YYYY-MM-DD HH:mm:ss'));
								const startHourString = initialData?.startHourString || "00:00";
								const endHourString = endTime.format("HH:mm");

								const episode: SelectedEpisode = {
									episodeId: selected.value.split(" ")[0], // Omit the title and duration from the value
									seriesId: initialData?.seriesId || "",
									start: startTime.toDate(),
									end: endTime.toDate(),
									startHourString: startHourString,
									endHourString: endHourString,
								};
								setSelectedEpisode(episode);
							}
						}}
						style={{ width: "100%" }}
						options={episodeOptions}
						listHeight={400}
						allowClear
					/>
				</Spin>
			</Space>
		</Modal>
	);
};

export default EpisodeEventModal;